// SEO-12 · /resources/school-fee-concessions-and-scholarships
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Fee-audit Batch 3 (CLAUDE.md "Scholarship & concession correctness"): apply_to_fees is ONE
//     atomic transaction; period-close is pre-checked across every candidate BEFORE the first
//     mutation; voided/cancelled/archived invoices are excluded; the idempotency anchor is the SUM
//     of already-posted tagged lines, so a retry applies only the remainder and reports the honest
//     cumulative total; an awarded+applied application is immutable until un-applied (409), and
//     un-apply posts a compensating POSITIVE line; award SoD fails CLOSED on a NULL requester; the
//     scheme amount must be > 0 and the award cap is unconditional; scheme delete is a soft-archive.
//   • Concession maker-checker: request → pending → approve/reject; approval posts a NEGATIVE
//     invoice line, clamped and idempotent per request; the `type_*` snapshot enforces the scheme
//     `max_amount` cap; approver ≠ requester unless the approver holds full access (G-SoD).
//   • ONE DERIVED relief register (`GET /fees/relief-register`), never a mirror write — D4-2.
//   • Period close returns 423 for a write into closed books (`core/period_close.py`).
// NO invented waiver volumes, no claim about a school's own audit outcome.

const article = {
  slug: "school-fee-concessions-and-scholarships",
  title: "Concessions and scholarships that survive an audit",
  seoTitle: "Fee Concessions That Survive an Audit | Vidyapeeth360",
  description:
    "Waivers are where school fee ledgers quietly break. How to record a concession so the discount, the approval and the reason still tie a year later.",
  topic: "Fees & finance",
  topicKey: "fees",
  readingMinutes: 6,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "A fee waiver is the only routine transaction in a school where money moves without money moving. That is exactly why it is the first thing an auditor asks about, and the first thing a spreadsheet loses.",
  moduleLinks: ["discount-and-scholarship", "fee-structure", "fee-reports"],
  conversion: {
    to: "/contact",
    label: "Walk your concession policy with us",
    note: "Bring last year's waiver list; we will map it onto approvals, ledger lines and what an auditor will ask for.",
  },
  sections: [
    {
      id: "the-shape-of-the-problem",
      heading: "A discount is a transaction, not an adjustment",
      blocks: [
        {
          t: "p",
          text: "Schools grant relief constantly: a staff ward, a second sibling, a merit scholarship, a trustee's decision about a family in difficulty. The money involved is real — often a meaningful share of the year's billing — but because nothing is received, it tends to get recorded as an edit rather than as an event.",
        },
        {
          t: "p",
          text: "Edit the invoice down and three things vanish: who authorised it, what it was for, and what the invoice originally said. A year later the school can see that a family paid ₹28,000 against a ₹28,000 invoice and cannot see that ₹12,000 was waived — which means it cannot answer how much relief it gave, to whom, or on what basis.",
        },
      ],
    },
    {
      id: "principles",
      heading: "Six rules that keep relief auditable",
      blocks: [
        {
          t: "ol",
          items: [
            "**Post it, never subtract it.** A waiver is a negative line on the invoice with its own reason and reference, not a smaller number in the original line. The invoice then still says what was billed, and separately what was relieved.",
            "**Two people, always.** Whoever requests a concession must not be the person who approves it. This is the single control that makes a waiver defensible, and the exception — a school owner acting alone — should be the visible exception rather than the norm.",
            "**Cap it at the scheme, in the system.** If the sibling concession is ₹5,000, an approval for ₹15,000 should be refused rather than noticed later. A policy that lives only in a circular is a policy that is not enforced.",
            "**One idempotent application.** Applying a scholarship twice — a double click, a retry, a re-run — must not double the relief. Anchor on what has already been posted, not on whether the button has been pressed before.",
            "**All or nothing across invoices.** A scholarship spread over four invoices that fails on the third leaves a student half-relieved and a ledger that reconciles to nothing. It should apply completely or not at all.",
            "**Reversal is a new entry.** Un-applying relief posts a compensating positive line; it does not delete the negative one. Both directions stay visible, which is the difference between a correction and a cover-up.",
          ],
        },
      ],
    },
    {
      id: "closed-books",
      heading: "The closed-period trap",
      blocks: [
        {
          t: "p",
          text: "Relief is often decided late — a hardship case settled in July for a term billed in April. If the books for April are closed, posting a waiver into them silently changes a period somebody has already reported.",
        },
        {
          t: "p",
          text: "The fix is not to forbid late relief; it is to check before you start. If a scholarship touches six invoices and one of them falls in a closed period, the whole application should stop before the first line is posted — not fail on the fourth and leave three students relieved and three not.",
        },
        {
          t: "note",
          title: "The reporting question this answers",
          text: "\"How much relief did we give this year, split by scheme and by class?\" should be a report, not a research project. It is only answerable if every waiver is a posted line carrying its scheme, its approver and its date — which is the same discipline that makes it auditable.",
        },
      ],
    },
    {
      id: "concession-vs-scholarship",
      heading: "Concession or scholarship — decide which you mean",
      blocks: [
        {
          t: "p",
          text: "The two words get used interchangeably and they behave differently. Keeping them apart simplifies the policy and the reporting.",
        },
        {
          t: "ul",
          items: [
            "**A concession** is a reduction against a specific invoice — a sibling discount, a staff-ward rate, a one-off relief. It is requested, approved and posted against that invoice.",
            "**A scholarship** is an award to a student from a scheme with a budget: assessed, granted, then applied across whichever invoices are outstanding. It has a lifecycle before it ever touches a fee.",
            "**Both end in the same place** — a negative line on an invoice with an audit trail — which is what lets a single report show total relief without pretending the two workflows are one.",
          ],
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "A concession is a request, not an edit: staff raise it against an invoice, it sits pending, and a second person with fee-approval rights decides. The approver cannot be the requester unless they hold full access, and approval posts a negative line clamped to the outstanding amount, idempotent per request, capped by the concession type's own limit — so an approval above the scheme's maximum is refused rather than reported afterwards.",
        },
        {
          t: "p",
          text: "A scholarship carries its own lifecycle — scheme, application, award, then application to fees. Awarding enforces separation of duties and fails closed rather than open when the requester is unknown; the award cannot exceed the scheme amount, and a scheme with no amount cannot award at all. Deleting a scheme archives it, so the applications made under it survive.",
        },
        {
          t: "p",
          text: "Applying an award is one transaction across every candidate invoice. Closed periods are checked for all of them before the first line is posted, void and cancelled invoices are skipped, and the idempotency anchor is the total already posted under that award — so a retry applies only the remainder and reports the honest cumulative figure rather than the full award a second time. An applied award is then immutable until it is explicitly un-applied, which posts a compensating positive line, restores the dues and re-opens the award.",
        },
        {
          t: "p",
          text: "Because both paths end as tagged invoice lines, the relief register is derived from the ledger rather than mirrored into a second table — concessions and scholarships in one view, with their source, per-channel totals and an export. An approver also sees the combined relief already on the invoice before deciding, so a student does not accumulate three overlapping discounts nobody noticed. The [scholarship workspace](/modules/discount-and-scholarship), the [fee structure](/modules/fee-structure) it discounts and the [fee reports](/modules/fee-reports) all read the same lines.",
        },
        {
          t: "p",
          text: "Set the schemes before the year starts, and make the exception require a second signature. Almost every unpleasant waiver conversation is really a conversation about something that was never written down.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
